import {Injectable} from "@angular/core";
import {LoggerService} from "../common/logger.service";
import {UsanaObject} from "../usana-object";
import {BehaviorSubject, Observable} from "rxjs";
import {IShopUser} from "../session/customer-models";
import {Constants} from "../constants";
import {Util} from "../util/util";
import {GqlNotificationService} from "../notification/graphql/gql-notification.service";
import {IProduct} from "../product/product-models";
import {ScriptService} from "../common/script.service";
import {map, tap} from "rxjs/operators";
import {Router} from "@angular/router";
import {GeneralErrorPopupComponent} from "../validation/general-error-popup.component";
import {BsModalService} from "ngx-bootstrap/modal";

@Injectable({
    providedIn: 'root'
})
export class SkinAnalyzerService extends UsanaObject {

    private PARTNER_ID_MAP: { [key: string]: string } = {
        HK: '1vEdbT2j1k', // Hong Kong
        MY: '8IcC2ZrJ1a', // Malaysia
        PH: 'uGT8wzTlqN', // Philippines
        SG: 'dg0BCgzyX7', // Singapore
        TW: 'MKCdnR0zcs', // Taiwan
    };

    public revieveScriptReady = new BehaviorSubject<boolean>(false);
    scriptLoaded: boolean = false;

    constructor(
        loggerService: LoggerService,
        private gqlNotificationService: GqlNotificationService,
        private scriptService: ScriptService,
        private modalService: BsModalService,
    ) {
        super(loggerService);
    }

    getBaseRevieveConfig(shopUser: IShopUser) {
        const revieveConfig = {
            partner_id: this.PARTNER_ID_MAP[shopUser.country.toUpperCase()],
            experienceId: 'UsanaSkinAnalyzer',
            locale: shopUser.customer.language,
            env: Util.inProd() ? "prod" : "test", // for test env. Change to prod for production deployment
            avoidCookies: false, // this is by default false and can be changed to true to avoid cookies
            // Set disableLauncherButton to true on the pages
            // where you want to hide the default advisor launcher button
            disableLauncherButton: true,
            // Set true to disable analytics
            analytics: {
                disableUsageAnalytics: false,
                disableProductAnalytics: false,
                disableErrorReporting: false,
            },
            onClose: () => {
                // User closed modal. Here you could forward them to checkout if they had
                // added products (onAddToCart was called before).
                this.log.debug('User clicked close');
            },
            onPageChange: (_index, page) => {
                this.log.debug("Page changed to: ", page);
                if (page.name == "recommendedProducts") {
                    this.getRevieveObject().API.hide();
                }
            },
        };
        return revieveConfig;
    }

    setupSkinAnalyzerResults(userData: any) {
        this.log.debug("Finding recommended products from user data: ", userData);
        const testResult: any = {
            usanaIds: this.getTopUsanaIdResults(userData?.data?.data?.products)
        };

        //If the user clicked "No, thanks" don't set the name and email (email won't be sent)
        if (userData?.data?.data?.activeSelections?.send_email === true) {
            testResult.name = userData?.data?.data?.activeSelections?.first_name;
            testResult.email = userData?.data?.data?.activeSelections?.email;
        }

        sessionStorage.setItem(Constants.SKIN_ANALYZER_RESULTS_INFO, JSON.stringify(testResult));

        //We want to create a new sharelist and send an email for every change
        sessionStorage.removeItem(Constants.SKIN_ANALYZER_SHARE_LIST_ID);
        sessionStorage.removeItem(Constants.SKIN_ANALYZER_EMAIL_PROCESSED);
    }

    getTopUsanaIdResults(revieveProducts: any): string[] {
        if (!revieveProducts?.morning?.economy && !revieveProducts?.evening?.economy) {
            return [];
        }

        const morningProducts = revieveProducts.morning?.economy || [];
        const eveningProducts = revieveProducts.evening?.economy || [];

        // Combine all products
        const allProducts = [...morningProducts, ...eveningProducts];

        // Get unique products by ID
        const uniqueProducts = allProducts.reduce((acc, product) => {
            if (!acc.find(p => p.id === product.id)) {
                acc.push(product);
            }
            return acc;
        }, []);

        // Sort by score descending and then by ID for stable sorting
        uniqueProducts.sort((a, b) => {
            const scoreDiff = b.score - a.score;
            return scoreDiff !== 0 ? scoreDiff : a.id.localeCompare(b.id);
        });

        // Extract IDs
        return uniqueProducts.map(product => product.id);
    }

    sendSkinAnalyzerEmail(skinAnalyzerResults: any, usanaIds: string[], shareListId: number): Observable<any> {
        this.log.debug("Sending skin analyzer email with input: ", skinAnalyzerResults);

        return this.gqlNotificationService.sendSkinAnalyzerEmail({
            name: skinAnalyzerResults?.name,
            email: skinAnalyzerResults?.email,
            usanaIds: usanaIds,
            shareListId: shareListId
        });
    }

    getFilteredProducts(productCards: any[]): string[] {
        const allUsanaIds = [];
        productCards.forEach(card => {
            if (card.productGroup?.products) {
                Object.values<IProduct>(card.productGroup.products).forEach(product => {
                    allUsanaIds.push(product.usanaId);
                });
            }
        });
        return allUsanaIds;
    }

    getRevieveObject(): any {
        return window['Revieve'];
    }

    initSkinAnalyzer(shopUser: IShopUser, router: Router): void {
        if(shopUser.config.showSkinAnalyzer === true) {
            const revieveConfig = this.getRevieveConfigGoToResultPage(shopUser, router);
            this.loadAndConfigureAnalyzer(revieveConfig);
        } else {
            this.log.warn("Skin Analyzer is not enabled for this user.");
            const initialState = {
                titleTag: 'cart.skin.analyzer.not.available.title',
                generalErrorsInterpolated: [{tag: 'cart.skin.analyzer.not.available'}],
            };
            this.modalService.show(GeneralErrorPopupComponent, {class: `modal-lg`, initialState});
        }
    }

    loadAndConfigureAnalyzer(revieveConfig: any): void {
        if (!this.scriptLoaded) {
            this.scriptService.addScriptToPage(Constants.SCRIPT_SKIN_ANALYZER).pipe(
                tap(() => this.scriptLoaded = true),
                map(() => this.initRevieveObject(revieveConfig))
            ).subscribe();
        } else {
            this.getRevieveObject()?.API.unmount();
            return this.initRevieveObject(revieveConfig);
        }
    }

    private initRevieveObject(revieveConfig: any): void {
        this.getRevieveObject().Init(revieveConfig, () => {
            this.revieveScriptReady.next(true);
        });
    }

    startAnalyzerFresh(): Observable<void> {
        return this.revieveScriptReady.pipe(
            map(scriptReady => {
                if (scriptReady) {
                    this.getRevieveObject()?.API.restart();
                    this.getRevieveObject()?.API.show();
                }
            })
        );
    }

    getRevieveConfigGoToResultPage(shopUser: IShopUser, router: Router): any {
        const revieveConfig: any = this.getBaseRevieveConfig(shopUser);
        revieveConfig.onDisplayProducts = (_historyId, _products, _sessionId) => {
            this.getRevieveObject().API.getAdvisorData((userData) => {
                this.log.debug("Revieve user data: ", userData); // Use this data to display product recommendations to the user
                this.getRevieveObject().API.navigateBack();
                this.setupSkinAnalyzerResults(userData);
                this.goToRecommendedProductsPage(router);
            });
        };
        return revieveConfig;
    }

    goToRecommendedProductsPage(router: Router) {
        this.log.debug("Going to usana recommended products page");
        router.navigate([Constants.SKIN_ANALYZER_RESULTS_STATE]);
    }

}
